import React from 'react';
import { useEditor } from '../../context/EditorContext';
import { ColorsDialog } from './ColorsDialog';
import { FormatObjectDialog } from './FormatObjectDialog';
import { ParagraphDialog, CharacterSpacingDialog, FontDialog } from './TextDialogs';
import { SymbolDialog } from './SymbolDialog';
import { SaveAsPictureDialog, BuildingBlockDialog } from './PictureDialogs';
import { BuildingBlockLibraryDialog } from './BuildingBlockLibraryDialog';
import { HyperlinkDialog, InsertTableDialog, InsertPageDialog, RenamePageDialog, RenameMasterDialog, PageSetupDialog, LayoutGuidesDialog, BusinessInfoDialog } from './MiscDialogs';
import { FillEffectsDialog } from './FillEffectsDialog';
import { ImportPublisherPanel } from './ImportPublisherPanel';
import { GoToPageDialog } from '../editor/FindReplacePane';
import { PaletteFromPictureDialog, RenameSchemeDialog } from './PaletteFromPictureDialog';
import { FontSubstitutionDialog } from './FontSubstitutionDialog';
import { FontSchemeDialog } from './FontSchemeDialog';
import { AutoflowDialog } from './AutoflowDialog';
import { EditSchemeDialog } from './EditSchemeDialog';
import { WpDialog, DialogBehindContext } from './DialogShell';

const REGISTRY = {
  colors: ColorsDialog,
  'format-object': FormatObjectDialog,
  paragraph: ParagraphDialog,
  'character-spacing': CharacterSpacingDialog,
  font: FontDialog,
  symbol: SymbolDialog,
  'save-as-picture': SaveAsPictureDialog,
  'building-block': BuildingBlockDialog,
  'building-block-library': BuildingBlockLibraryDialog,
  hyperlink: HyperlinkDialog,
  'insert-table': InsertTableDialog,
  'insert-page': InsertPageDialog,
  'rename-page': RenamePageDialog,
  'rename-master': RenameMasterDialog,
  'page-setup': PageSetupDialog,
  'layout-guides': LayoutGuidesDialog,
  'business-info': BusinessInfoDialog,
  'fill-effects': FillEffectsDialog,
  'go-to-page': GoToPageDialog,
  'palette-from-picture': PaletteFromPictureDialog,
  'rename-scheme': RenameSchemeDialog,
  'edit-scheme': EditSchemeDialog,
  'font-substitution': FontSubstitutionDialog,
  'font-scheme': FontSchemeDialog,
  autoflow: AutoflowDialog,
};

const BACKSTAGE_ROUTES = { 'save-project': 'save', 'save-as-project': 'save-as', 'open-project': 'open', 'export-project': 'export', 'new-project': 'new', 'print-project': 'print', 'share-project': 'share', 'home-project': 'home' };

/** Renders whichever dialog is open in editor state (state.dialog = { name, props }). */
export const DialogHost = () => {
  const { state, closeDialog, rawDispatch } = useEditor();
  const d = state.dialog;

  React.useEffect(() => {
    if (d && BACKSTAGE_ROUTES[d.name]) {
      rawDispatch({ type: 'SET_FILE_OPEN', payload: { open: true, section: BACKSTAGE_ROUTES[d.name] } });
      closeDialog();
    }
  }, [d, rawDispatch, closeDialog]);

  if (!d || BACKSTAGE_ROUTES[d.name]) return null;
  if (d.name === 'import-pub') {
    return (
      <WpDialog name="import-pub" title="Import Microsoft Publisher File" description="Convert a .pub file into an editable publication, or download it as PDF / PNG." width="w-[720px]" onCancel={closeDialog} cancelLabel="Close">
        <ImportPublisherPanel onOpened={closeDialog} />
      </WpDialog>
    );
  }
  const Cmp = REGISTRY[d.name];
  if (!Cmp) return null;
  // Dialogs underneath the open one (Fill Effects while its stop's Colours dialog is on top) stay mounted — their state
  // and callbacks live on, the picked colour lands where it was asked for — but are inert until the top one closes.
  // Every dialog gets the same element shape + key whether it is on top or underneath, so coming back to the top does
  // not remount it (a remount would lose its edits and its "original" for Cancel).
  const list = [...(state.dialogStack || []).filter((b) => REGISTRY[b.name]).map((b) => ({ ...b, behind: true })), { ...d, behind: false }];
  return (
    <>
      {list.map((item) => { const C = REGISTRY[item.name]; return <DialogBehindContext.Provider key={`dialog-${item.name}`} value={item.behind}><C props={item.props || {}} /></DialogBehindContext.Provider>; })}
    </>
  );
};
